import { motion } from "framer-motion";
import { Calendar, CheckCircle, Lightbulb } from "lucide-react";

export const ScratchCourse = () => {
  const scratchLevels = [
    {
      level: "Level 1",
      title: "Getting Started with Scratch",
      color: "text-green-800 border-green-200",
      items: [
        "Introduction to the Scratch interface",
        "Understanding Motion Blocks",
        "Looks and Sound Blocks",
        "Using Events",
        "Creating your first animation",
      ],
    },
    {
      level: "Level 2",
      title: "Building Logic and Control",
      color: "text-yellow-800 border-yellow-200",
      items: [
        "Sequencing",
        "Loops (Repeat & Forever)",
        "Conditionals (If / If-Else)",
        "Sensing Blocks",
        "Variables",
        "Operators",
        "Making your first interactive story or quiz",
      ],
    },
    {
      level: "Level 3",
      title: "Game Design and Interactivity",
      color: "text-orange-800 border-orange-200",
      items: [
        "Planning and designing a simple game",
        "Sprite interactions",
        "Broadcast and Messaging",
        "Adding scoring systems and lives",
        "Using timers and levels",
        "Debugging – finding and fixing errors",
        "Creating classic games like Maze, Pong, or Catch-the-Fruit",
      ],
    },
    {
      level: "Level 4",
      title: "Creativity and Customization",
      color: "text-blue-800 border-blue-200",
      items: [
        "Designing custom sprites and backgrounds",
        "Adding sound effects and music",
        "Using costume changes for animation",
        "Drawing with the Pen Tool",
        "Creating interactive stories and digital art",
      ],
    },
    {
      level: "Level 5",
      title: "Advanced Scratch Concepts",
      color: "text-purple-800 border-purple-200",
      items: [
        "Cloning Sprites – duplicating objects in games",
        "My Blocks (Functions) – creating custom reusable code",
        "Cloud Variables (for advanced users) – saving scores online",
        "Simulations and Experiments – modeling real-world problems",
        "Integrating with physical devices (e.g., micro:bit or LEGO WeDo)",
        "Culminating project: build your own Scratch game or story",
      ],
    },
  ];

  return (
    <div className="w-full bg-gray-50 min-h-screen">
      <div className="bg-green-600 text-white py-20 px-4 text-center">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-4"
        >
          Scratch Programming
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xl max-w-2xl mx-auto opacity-90"
        >
          Course 3: Learn to code by dragging and dropping colourful blocks.
        </motion.p>
      </div>

      <section className="py-16 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-gray-600 max-w-2xl mx-auto">
            Scratch is a visual programming language created by MIT to help children learn how to code by dragging and
            dropping colourful blocks instead of typing complex code.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-gray-600 mt-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <span>Duration: 4-6 months (level-wise enrolment available)</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Age: 6-10 years</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {scratchLevels.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-xl border-2 ${level.color} bg-white`}
              >
                <div
                  className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 ${
                    level.color.split(" ")[0]
                  } bg-opacity-50`}
                >
                  {level.level}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{level.title}</h3>
                <ul className="space-y-2">
                  {level.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          <div className="lg:col-span-1 space-y-8">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src="/images/scratch-logo.png" alt="Scratch Programming" className="w-full h-auto" />
            </div>
            <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Lightbulb className="w-6 h-6 text-yellow-400" />
                What will you learn?
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-yellow-400 mb-2">Programming Concepts</h4>
                  <p className="text-gray-300 text-sm">
                    Sequencing, loops, conditionals, variables, events, functions, debugging
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow-400 mb-2">Creative Expression</h4>
                  <p className="text-gray-300 text-sm">Storytelling, game design, art & music, design thinking</p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow-400 mb-2">Soft Skills</h4>
                  <p className="text-gray-300 text-sm">Logical thinking, collaboration, persistence, creativity</p>
                </div>
              </div>
            </div>
              <div className="rounded-2xl overflow-hidden shadow-lg scale-x-[-1]">
              <img src="/images/scratch-cat-logo.png" alt="Scratch Programming" className="w-full h-auto" />
            </div>

            
          </div>
        </div>
      </section>
    </div>
  );
};
